import {
    buildConnectorParamKey,
    LEGACY_CONNECTOR_KEYS,
    legacyConnectorMatches,
    parseConnectorParamKey,
    SsoLoginMode,
    SsoProtocol,
} from "src/pages/Authentication/ssoConnector";
import { mapViolationsToFields } from "./ssoProviderModel";

export type AppParamAction = { kind: "upsert"; isNew: boolean; value: string } | { kind: "delete" } | { kind: "noop" };

export const decideAppParamAction = (value: string | null, stored: string | undefined): AppParamAction => {
    if (value === null || value === "") {
        return stored === undefined ? { kind: "noop" } : { kind: "delete" };
    }

    return { kind: "upsert", isNew: stored === undefined, value };
};

export type AppParamPorts = {
    upsert: (args: { key: string; isNew: boolean; value: string }) => Promise<unknown>;
    remove: (args: { key: string }) => Promise<unknown>;
    uploadLogo: (args: { key: string; file: File }) => Promise<unknown>;
    refetch: () => Promise<unknown>;
};

export const LOGIN_MODE_KEY = "sso-login-mode";

const apply = (ports: AppParamPorts, key: string, value: string | null, stored: string | undefined) => {
    const action = decideAppParamAction(value, stored);

    if (action.kind === "noop") return Promise.resolve();
    if (action.kind === "delete") return ports.remove({ key });

    return ports.upsert({ key, isNew: action.isNew, value: action.value });
};

export const persistLoginMode = async (
    mode: SsoLoginMode,
    stored: string | undefined,
    ports: AppParamPorts,
): Promise<boolean> => {
    try {
        await apply(ports, LOGIN_MODE_KEY, mode, stored);

        return true;
    } catch {
        return false;
    } finally {
        await ports.refetch();
    }
};

export type ProviderButtonDraft = {
    selectedLogo: File | null;
    isLogoCleared: boolean;
};

export type ProviderButtonStored = {
    logo: string | undefined;
};

export const readProviderButton = (
    parameters: Record<string, string | undefined>,
    protocol: SsoProtocol,
    providerName: string,
): ProviderButtonStored => {
    const logoKey = buildConnectorParamKey("logo", protocol, providerName);

    return { logo: logoKey === null ? undefined : parameters[logoKey] };
};

export const persistProviderButton = async (
    protocol: SsoProtocol,
    providerName: string,
    draft: ProviderButtonDraft,
    stored: ProviderButtonStored,
    ports: AppParamPorts,
): Promise<void> => {
    const logoKey = buildConnectorParamKey("logo", protocol, providerName);

    if (logoKey === null) return;

    if (draft.selectedLogo !== null) {
        await ports.uploadLogo({ key: logoKey, file: draft.selectedLogo });
        return;
    }

    if (draft.isLogoCleared && stored.logo !== undefined) {
        await ports.remove({ key: logoKey });
    }
};

export const forgetProviderButton = async (
    protocol: SsoProtocol,
    providerName: string,
    ports: AppParamPorts,
    parameters: Record<string, string | undefined> = {},
): Promise<void> => {
    for (const key of Object.keys(parameters)) {
        const parsed = parseConnectorParamKey(key);
        if (parsed?.protocol === protocol && parsed.providerName === providerName) await ports.remove({ key });
    }

    if (!legacyConnectorMatches(parameters, protocol, providerName)) return;

    for (const key of LEGACY_CONNECTOR_KEYS) {
        if (parameters[key] !== undefined) await ports.remove({ key });
    }
};

export type ProviderSaveOutcome =
    | { kind: "saved" }
    | { kind: "provider-rejected"; violations: Record<string, string> }
    | { kind: "provider-failed" }
    | { kind: "button-failed" };

export type ProviderSaveOperations = {
    persistButton: () => Promise<unknown>;
    refetch: () => Promise<unknown>;
    saveProvider: () => Promise<unknown>;
};

export const saveProviderWithButton = async (operations: ProviderSaveOperations): Promise<ProviderSaveOutcome> => {
    try {
        await operations.saveProvider();
    } catch (error) {
        const violations = mapViolationsToFields(error);
        await operations.refetch();

        return Object.keys(violations).length > 0 ?
                { kind: "provider-rejected", violations }
            :   { kind: "provider-failed" };
    }

    let outcome: ProviderSaveOutcome = { kind: "saved" };

    try {
        await operations.persistButton();
    } catch {
        outcome = { kind: "button-failed" };
    }

    await operations.refetch();

    return outcome;
};
